import React, {useState, useRef} from 'react';
import Typography from "@mui/material/Typography";
import {Button, Container, Grid, Icon, MenuItem} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import clsx from "clsx";
import NautilusTextInput from "../Common/NautilusTextInput";
import {makeStyles} from "@mui/styles";
import {MobileDateRangePicker, LocalizationProvider} from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {format, differenceInDays} from "date-fns";
import {useAuthUser, useIsAuthenticated} from "react-auth-kit";
import {requestApi, showAlertAutoDis} from "../../utils/utils";

const useStyles = makeStyles(() => ({
    button: {
        lineHeight: 2.5,
        fontSize: '1.4rem',
        backgroundColor: '#00b7f0',
        borderColor: '#00b7f0',
    },
    root: {
        '& .MuiPaper-elevation1': {
            boxShadow: 'none'
        }
    }
}));

const initialForm = {
    pax: '',
    notes: '',
    dates: [null, null]
}


const CharterForm = ({ship}) => {
    const [charterForm, setCharterForm] = useState(initialForm);
    const [estimatedCost, setEstimatedCost] = useState(0);
    const recaptchaRef = useRef();
    const classes = useStyles();
    const isAuth = useIsAuthenticated();
    const auth = useAuthUser();
    const commission = isAuth() ? auth().commission : 0;

    const handleForm = (event) => {
        const {target: {name, value}} = event;
        setCharterForm({...charterForm, [name]: value})
    };

    const handleRangeChange = (datesArray = [null, null]) => {
        setCharterForm({...charterForm, dates: datesArray});
        handleSelectedRange(datesArray);
    };

    const handleDisableDate = (date) => {
        const formattedDate = format(date, 'yyyy-MM-dd');
        return ship.bookedDates.includes(formattedDate);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (charterForm.dates.some(x => x === null))
            return showAlertAutoDis({title: 'Error', text: 'Please select a valid date range.'});

        if (!charterForm.pax)
            return showAlertAutoDis({title: 'Error', text: 'Please select the number of guests.'});
        await recaptchaRef.current.executeAsync();
        const url = `/save-charter-request/${ship.shortcode}`;
        const params = {
            startDate: format(charterForm.dates[0], 'yyyy-MM-dd'),
            endDate: format(charterForm.dates[1], 'yyyy-MM-dd'),
            passengers: charterForm.pax,
            notes: charterForm.notes
        };

        const {data} = await requestApi(url, 'POST', params);
        if (data.status === 'success')
            return showAlertAutoDis({title: 'Success', text: data.message});
        else
            return showAlertAutoDis({title: 'Error', text: data.message});

    };

    const handleSelectedRange = (dates) => {
        if (dates.some(x => x === null))
            return setEstimatedCost(0);

        const difference = differenceInDays(dates[1], dates[0]);
        const numOfWeeks = Math.floor(difference / 7);
        const numOfDays = difference % 7;
        const dailyRate = parseFloat(ship.dailyRate);
        const weeklyRate = parseFloat(ship.weeklyRate);
        const cost = (weeklyRate * numOfWeeks) + (dailyRate * numOfDays);
        setEstimatedCost(cost * (1 + parseFloat(ship.taxRate)));
    };


    return (
        <Container maxWidth={'lg'} style={{minWidth: 350, paddingLeft: 0, paddingRight: 0}} className={classes.root}>
            <Typography variant="h3" component="h3" className={'tripTitle'} sx={{paddingBottom: 5}}>
                Charter Request
            </Typography>
            <form className={'formPayment'} noValidate onSubmit={handleSubmit}>
                <Grid container direction="row" justify="center" alignItems="center" style={{paddingBottom: 30}}>
                    <Grid item xs={12} style={{padding: 5}}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <MobileDateRangePicker
                                value={charterForm.dates}
                                onChange={() => {}}
                                onAccept={handleRangeChange}
                                startText={'From'}
                                endText={'To'}
                                cancelText={''}
                                disableCloseOnSelect={false}
                                disablePast
                                shouldDisableDate={handleDisableDate}
                                renderInput={(start, end) => (
                                    <>
                                        <Grid item xs={6} style={{paddingRight: '5px'}}>
                                            <NautilusTextInput fullWidth {...start}></NautilusTextInput>
                                        </Grid>
                                        <Grid item xs={6} style={{paddingLeft: '5px'}}>
                                            <NautilusTextInput fullWidth {...end}></NautilusTextInput>
                                        </Grid>
                                    </>
                                )}
                            />

                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={6} style={{padding: 5}}>
                        <NautilusTextInput fullWidth name="pax" value={charterForm['pax']}
                                           variant="outlined" select label="Number of Guests"
                                           onChange={handleForm}>
                            <MenuItem value={''}>
                                Select
                            </MenuItem>
                            {Array.from(Array(ship.capacity), (_, i) => i + 1).map(pax => (
                                <MenuItem key={pax} value={pax}>{pax}</MenuItem>
                            ))}

                        </NautilusTextInput>
                    </Grid>
                    <Grid item xs={12} style={{padding: 5}}>
                        <NautilusTextInput fullWidth name="notes" value={charterForm['notes']}
                                           placeholder={'Notes'} onChange={handleForm} multiline
                                           rows={4} label="Comments, Notes, Requests" variant="outlined"/>
                    </Grid>
                    <Grid item xs={12}>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={window.recaptchaKey}
                            size="invisible"
                        />
                    </Grid>
                    {estimatedCost > 0 && (<Grid item xs={12} style={{padding: 5}}>
                        <Typography variant="h5" style={{fontSize: '2.4rem'}}>
                            Estimated Cost: <span>$ {estimatedCost.formatMoney(2)}</span>
                        </Typography>
                        {commission && <Typography variant="h5" style={{fontSize: '2.4rem'}}>
                            Commission: <span>$ {(estimatedCost * commission).formatMoney(2)}</span>
                        </Typography>}
                    </Grid>)}

                    <Grid item xs={12} style={{padding: 5}}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            fullWidth
                            className={clsx({[classes.button]: true, 'payButton': true})}
                            startIcon={<Icon>send</Icon>}
                        >
                            Submit Charter Request
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
};

export default CharterForm;